export interface MapPoint {
  lat: number;
  lng: number;
}

// Google writes the space after the comma as "+" in its links ("27.14,+-13.19").
const COORDS = String.raw`(-?\d{1,3}(?:\.\d+)?)[\s+]*,[\s+]*(-?\d{1,3}(?:\.\d+)?)`;

// The pair must end there: "12,14+Rue+Ibn+Sina" is an address, not a position. "33.5,-7.6(Shop)" is a labelled position.
const COORDS_END = String.raw`(?=[\s+]*(?:$|[/?#&(]))`;

function valid(lat: number, lng: number): MapPoint | null {
  return Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180 ? { lat, lng } : null;
}

/**
 * The pin a Google Maps link points at: the place pin (`!3d..!4d..`) first, then the `q`, `query`, `ll` or
 * `center` parameter, then coordinates in the path (`/maps/search/lat,lng`), then the map centre (`@lat,lng`), and
 * last a bare "lat, lng" pair. Short links (maps.app.goo.gl) hide their coordinates behind a redirect and give
 * null here: MapsLinkService expands them first.
 */
export function parseGoogleMapsUrl(text: string | null | undefined): MapPoint | null {
  const raw = (text ?? "").trim();
  if (!raw) {
    return null;
  }
  let decoded = raw;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    /* keep the raw text */
  }
  const place = decoded.match(/!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/);
  if (place) {
    return valid(Number(place[1]), Number(place[2]));
  }
  const param = decoded.match(new RegExp("[?&](?:q|query|ll|center|destination)=" + COORDS + COORDS_END));
  if (param) {
    return valid(Number(param[1]), Number(param[2]));
  }
  const inPath = decoded.match(new RegExp("/maps/(?:search|place)/" + COORDS + COORDS_END));
  if (inPath) {
    return valid(Number(inPath[1]), Number(inPath[2]));
  }
  const centre = decoded.match(new RegExp("@" + COORDS));
  if (centre) {
    return valid(Number(centre[1]), Number(centre[2]));
  }
  const bare = decoded.match(new RegExp("^" + COORDS + "$"));
  return bare ? valid(Number(bare[1]), Number(bare[2])) : null;
}

/** The place a link names when it holds no coordinates ("/maps/place/Cafe+Atlas/..." or "?q=Cafe+Atlas"), to look it up. */
export function mapsPlaceQuery(text: string | null | undefined): string | null {
  const raw = (text ?? "").trim();
  if (!/google\.[a-z.]+\/maps|maps\.google\./i.test(raw) || parseGoogleMapsUrl(raw)) {
    return null;
  }
  const found = raw.match(/\/maps\/(?:place|search)\/([^/?#]+)/) ?? raw.match(/[?&](?:q|query)=([^&#]+)/);
  if (!found) {
    return null;
  }
  try {
    return decodeURIComponent(found[1].replace(/\+/g, " ")).trim() || null;
  } catch {
    return null;
  }
}

/** The link that opens a pin in Google Maps, the one written back in the URL field when the pin moves. */
export function buildGoogleMapsUrl(lat: number, lng: number): string {
  return `https://www.google.com/maps?q=${lat.toFixed(6)},${lng.toFixed(6)}`;
}

/** True for a link that looks like Google Maps but cannot be read offline (short share link). */
export function isShortGoogleMapsUrl(text: string | null | undefined): boolean {
  return /(^|\/\/)(maps\.app\.goo\.gl|goo\.gl\/maps)/i.test((text ?? "").trim());
}
